import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cat, Check, Loader2, MessageSquareText, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

export default function SmsOptIn() {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { toast.error('Enter your mobile number'); return; }
    if (!consent) { toast.error('Please check the box to give consent'); return; }
    setBusy(true);
    try {
      await api.post('/sms/opt-in', { phone, name, consent });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not sign you up. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-10">
      <div className="mx-auto max-w-lg">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[hsl(var(--coral))]">
            <Cat className="h-5 w-5 text-white" />
          </div>
          <span className="text-lg font-extrabold">Purrflect</span>
        </div>

        {done ? (
          <div className="mt-10 rounded-3xl bg-white p-8 text-center card-soft" data-testid="sms-optin-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
              <Check className="h-7 w-7 text-green-600" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold">You're subscribed!</h1>
            <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
              We've sent a confirmation text to <span className="font-semibold text-foreground">{phone}</span>. You'll now
              receive care reminders and urgent pet-health alerts. Reply <span className="font-bold">STOP</span> anytime to unsubscribe,
              or <span className="font-bold">HELP</span> for help.
            </p>
            <Link to="/" className="coral-btn mt-6 inline-flex rounded-2xl px-6 py-3 text-sm font-bold text-white">Back to Purrflect</Link>
          </div>
        ) : (
          <>
            <div className="mt-6 flex items-center gap-2 text-[hsl(var(--coral))]">
              <MessageSquareText className="h-5 w-5" />
              <h1 className="text-2xl font-extrabold text-foreground">Get Purrflect text alerts</h1>
            </div>
            <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
              Opt in to receive <span className="font-semibold text-foreground">care reminders</span> (vaccines, vet visits,
              medications) and <span className="font-semibold text-foreground">urgent pet-health alerts</span> for your cat by text message.
            </p>

            <form onSubmit={submit} className="mt-6 rounded-3xl bg-white p-6 card-soft">
              <label className="text-sm font-bold" htmlFor="sms-name">Your name (optional)</label>
              <input
                id="sms-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane"
                data-testid="sms-optin-name"
                className="mt-1.5 h-12 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]"
              />

              <label className="mt-4 block text-sm font-bold" htmlFor="sms-phone">Mobile number</label>
              <input
                id="sms-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                placeholder="+1 555 123 4567"
                data-testid="sms-optin-phone"
                className="mt-1.5 h-12 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]"
              />

              <label className="mt-5 flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="sms-optin-consent"
                  className="mt-1 h-5 w-5 shrink-0 accent-[hsl(var(--coral))]"
                />
                <span className="text-[13px] leading-relaxed text-muted-foreground">
                  I agree to receive account-related text messages from <span className="font-semibold text-foreground">Purrflect</span> (care
                  reminders and urgent pet-health alerts) at the number I provided. Consent is not a condition of any purchase.
                  <span className="font-semibold text-foreground"> Message frequency will vary according to your specific needs. Purrflect does not send marketing texts.</span> Msg &amp; data
                  rates may apply. Reply <span className="font-bold">HELP</span> for help, <span className="font-bold">STOP</span> to unsubscribe.
                  See our <Link to="/terms" className="underline">Terms of Service</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
                </span>
              </label>

              <button
                type="submit"
                disabled={busy}
                data-testid="sms-optin-submit"
                className="coral-btn mt-6 flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-base font-bold text-white disabled:opacity-70"
              >
                {busy && <Loader2 className="h-5 w-5 animate-spin" />}Yes, sign me up!
              </button>

              <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4" />
                We do not share your mobile number or opt-in data with third parties for marketing.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
